import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

export function FloatingAuthModal() {
  const { user, signInWithGoogle } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Don't show if user is authenticated (not anonymous) or if dismissed
  const isAuthenticated = user && !user.is_anonymous;
  if (isAuthenticated || isDismissed) return null;

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      toast({
        title: 'Google sign in failed',
        description:
          error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Sign in"
      className="fixed bottom-6 right-6 z-50 w-[340px] overflow-hidden rounded-xl border border-white/10 bg-[#191A1A]/95 shadow-2xl backdrop-blur-md transition-all duration-300 animate-in fade-in slide-in-from-bottom-4"
    >
      {/* Header with close button */}
      <div className="absolute right-4 top-4 z-10">
        <button
          onClick={() => setIsDismissed(true)}
          className="rounded-full p-1 text-adam-neutral-400 transition-colors hover:bg-white/5 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-6 pt-10">
        <div className="flex flex-col gap-5">
          {/* Logo and Title */}
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-adam-blue/20 to-transparent">
              <img
                src={`${import.meta.env.BASE_URL}/daddycad-logo.png`}
                alt="Logo"
                className="h-6 w-6"
              />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold leading-tight text-white">
                Sign in to DaddyCad
              </h3>
              <p className="text-sm text-adam-neutral-400">
                Save and access your files
              </p>
            </div>
          </div>

          {/* Google Button */}
          <Button
            type="button"
            className="group relative w-full gap-2 overflow-hidden rounded-xl bg-[#00A6FF] py-5 text-sm font-medium text-white shadow-[0_0_20px_-5px_rgba(0,166,255,0.4)] transition-all hover:bg-[#0095E6] hover:shadow-[0_0_25px_-5px_rgba(0,166,255,0.5)]"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="currentColor"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="currentColor"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="currentColor"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="currentColor"
              />
            </svg>
            <span>{isLoading ? 'Signing in...' : 'Continue with Google'}</span>
          </Button>

          <p className="text-center text-xs text-adam-neutral-500">
            By continuing, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  );
}

